import { jsx, Fragment, jsxs } from "react/jsx-runtime";
import { css } from "@/styled-system/css";
import { Box, FormField, FormHelp, AdornedInput, Label, Text, Slider, Paragraph } from "@zillow/constellation-10";
import { event } from "@zillow/universal-analytics";
import { useState, useRef, useEffect } from "react";
const RENT_AFFORDABILITY_STORAGE_KEY = "rent-affordability-max-rent";
const inputCss = css.raw({
  backgroundColor: "gray.50",
  boxShadowColor: "gray.300",
  _hover: {
    boxShadowColor: "border.action.hero.default"
  }
});
const RentAffordabilityCalculator = ({
  monthlyNetIncome,
  monthlyDebts,
  monthlySavings,
  monthlyExpenses
}) => {
  const [incomeAndDebt, setIncomeAndDebt] = useState({
    monthlyNetIncomePretax: 11600,
    monthlyNetIncome: Number(monthlyNetIncome),
    monthlyDebts: Number(monthlyDebts),
    monthlySavings: Number(monthlySavings),
    monthlyExpenses: Number(monthlyExpenses)
  });
  const [rent, setRent] = useState(Math.round(incomeAndDebt.monthlyNetIncomePretax * 0.33));
  const [errors, setErrors] = useState({
    monthlyNetIncome: "",
    monthlyDebts: "",
    monthlySavings: "",
    monthlyExpenses: ""
  });
  const overBudget = incomeAndDebt.monthlyNetIncome - rent - incomeAndDebt.monthlyDebts - incomeAndDebt.monthlySavings - incomeAndDebt.monthlyExpenses < 0;
  const originalValuesRef = useRef({});
  const storeLatestPrefocusedValueOnFocus = (e, label) => {
    originalValuesRef.current[label] = e.target.value;
  };
  const handleBlurAndSendEventIfModified = (e, label) => {
    const oldVal = originalValuesRef.current[label];
    const newVal = e.target.value;
    if (newVal !== oldVal) {
      sendInteractionEvent(label);
    }
    if (!newVal) {
      e.target.value = "0";
      switch (label) {
        case "Monthly net income":
          updateIncomeAndDebt(e, "monthlyNetIncome");
          break;
        case "Monthly debts":
          updateIncomeAndDebt(e, "monthlyDebts");
          break;
      }
    }
  };
  const sendInteractionEvent = (label) => {
    const cmsInfo = {
      block_type_cd: "form_field",
      block_id: "rent_afffordability_calc",
      block_displayed_txt: label
    };
    const newLaneData = {
      envelope: {
        event_type_id: "2056",
        event_type_version_id: "2",
        event_template_id: "126",
        event_template_version_id: "1"
      },
      clickstream_trigger: {
        trigger_type_nm: "interaction",
        trigger_location_nm: "cms",
        trigger_source_nm: "cms_form",
        trigger_object_nm: "no_trigger_object"
      },
      semantic: {
        semantic_event_nm: "cms_engagement"
      },
      cms_info: cmsInfo
    };
    event(newLaneData);
  };
  const updateIncomeAndDebt = (e, type) => {
    let updatedIncomeAndDebt = {};
    let updatedNetIncome;
    let updatedNetIncomePretax;
    let expenseValueValidity = "";
    const expenseValue = parseFloat(e.target.value);
    if (type === "monthlyNetIncome") {
      if (!expenseValue && expenseValue !== 0) {
        expenseValueValidity = "Please enter valid income value";
        updatedNetIncome = 0;
        updatedNetIncomePretax = 0;
      } else {
        updatedNetIncome = expenseValue;
        updatedNetIncomePretax = Math.round(updatedNetIncome / 0.75);
      }
      updatedIncomeAndDebt = {
        monthlyNetIncomePretax: updatedNetIncomePretax,
        monthlyNetIncome: updatedNetIncome
      };
      setRent(Math.max(100, Math.round(updatedNetIncomePretax * 0.33)));
    }
    if (type === "monthlyDebts" || type === "monthlySavings" || type === "monthlyExpenses") {
      if (!expenseValue && expenseValue !== 0) {
        expenseValueValidity = "Please enter valid expense value";
      }
      updatedIncomeAndDebt = {
        [type]: expenseValue || 0
      };
    }
    setIncomeAndDebt((prevState) => ({
      ...prevState,
      ...updatedIncomeAndDebt
    }));
    setErrors((prevState) => ({
      ...prevState,
      [type]: expenseValueValidity
    }));
  };
  const sliderMin = Math.max(100, Math.round((incomeAndDebt.monthlyNetIncomePretax || 0) * 0.1));
  const sliderMax = Math.max(sliderMin + 100, Math.round((incomeAndDebt.monthlyNetIncomePretax || 0) * 0.4));
  const sliderStep = Math.max(1, Math.round((sliderMax - sliderMin) / 30));
  useEffect(() => {
    try {
      sessionStorage.setItem(RENT_AFFORDABILITY_STORAGE_KEY, String(rent));
    } catch (_) {
    }
  }, [rent]);
  const handleSliderChange = (newValue) => {
    const value = Array.isArray(newValue) ? newValue[0] : newValue;
    setRent(value);
  };
  const handleSliderCommit = () => {
    sendInteractionEvent("Rent Affordability slider");
  };
  const percentOfIncome = incomeAndDebt.monthlyNetIncomePretax ? `${Math.round(rent / incomeAndDebt.monthlyNetIncomePretax * 100)}%` : "N/A";
  return /* @__PURE__ */ jsx(Fragment, { children: /* @__PURE__ */ jsxs(Box, { css: {
    display: "flex",
    flexDirection: {
      base: "column",
      lg: "row"
    },
    gap: "looser",
    alignItems: "center"
  }, children: [
    /* @__PURE__ */ jsx(Box, { css: {
      flexShrink: 0
    }, children: /* @__PURE__ */ jsx(Box, { css: {
      width: "382px"
    }, children: /* @__PURE__ */ jsxs(Box, { css: {
      display: "flex",
      flexDirection: "column",
      gap: "default"
    }, children: [
      /* @__PURE__ */ jsx(FormField, { label: /* @__PURE__ */ jsx(Label, { required: true, children: "Monthly net income" }), error: errors.monthlyNetIncome !== "", control: /* @__PURE__ */ jsx(AdornedInput, { css: inputCss, startAdornment: /* @__PURE__ */ jsx(AdornedInput.Adornment, { "aria-hidden": "true", children: "$" }), input: /* @__PURE__ */ jsx(AdornedInput.Input, { type: "number", placeholder: "Income after taxes", defaultValue: incomeAndDebt.monthlyNetIncome, maxLength: 10, onChange: (e) => {
        updateIncomeAndDebt(e, "monthlyNetIncome");
      }, onFocus: (e) => storeLatestPrefocusedValueOnFocus(e, "Monthly net income"), onBlur: (e) => handleBlurAndSendEventIfModified(e, "Monthly net income") }) }), description: /* @__PURE__ */ jsx(FormHelp, { children: errors.monthlyNetIncome }) }),
      /* @__PURE__ */ jsx(FormField, { label: /* @__PURE__ */ jsx(Label, { required: true, children: "Monthly debts" }), error: errors.monthlyDebts !== "", control: /* @__PURE__ */ jsx(AdornedInput, { css: inputCss, startAdornment: /* @__PURE__ */ jsx(AdornedInput.Adornment, { "aria-hidden": "true", children: "$" }), input: /* @__PURE__ */ jsx(AdornedInput.Input, { type: "number", placeholder: "Credit cards, loans, ...", defaultValue: incomeAndDebt.monthlyDebts, maxLength: 7, onChange: (e) => {
        updateIncomeAndDebt(e, "monthlyDebts");
      }, onFocus: (e) => storeLatestPrefocusedValueOnFocus(e, "Monthly debts"), onBlur: (e) => handleBlurAndSendEventIfModified(e, "Monthly debts") }) }), description: /* @__PURE__ */ jsx(FormHelp, { children: errors.monthlyDebts }) }),
      /* @__PURE__ */ jsx(FormField, { label: /* @__PURE__ */ jsx(Label, { children: "Monthly Savings" }), error: errors.monthlySavings !== "", control: /* @__PURE__ */ jsx(AdornedInput, { css: inputCss, startAdornment: /* @__PURE__ */ jsx(AdornedInput.Adornment, { "aria-hidden": "true", children: "$" }), input: /* @__PURE__ */ jsx(AdornedInput.Input, { type: "number", placeholder: "Savings, 401k, ...", defaultValue: incomeAndDebt.monthlySavings, maxLength: 7, onChange: (e) => {
        updateIncomeAndDebt(e, "monthlySavings");
      }, onFocus: (e) => storeLatestPrefocusedValueOnFocus(e, "Monthly savings"), onBlur: (e) => handleBlurAndSendEventIfModified(e, "Monthly savings") }) }), description: /* @__PURE__ */ jsx(FormHelp, { children: errors.monthlySavings }) }),
      /* @__PURE__ */ jsx(FormField, { label: /* @__PURE__ */ jsx(Label, { children: "Monthly Expenses" }), error: errors.monthlyExpenses !== "", control: /* @__PURE__ */ jsx(AdornedInput, { css: inputCss, startAdornment: /* @__PURE__ */ jsx(AdornedInput.Adornment, { "aria-hidden": "true", children: "$" }), input: /* @__PURE__ */ jsx(AdornedInput.Input, { type: "number", placeholder: "Food, insurance, ...", defaultValue: incomeAndDebt.monthlyExpenses, maxLength: 7, onChange: (e) => {
        updateIncomeAndDebt(e, "monthlyExpenses");
      }, onFocus: (e) => storeLatestPrefocusedValueOnFocus(e, "Monthly expenses"), onBlur: (e) => handleBlurAndSendEventIfModified(e, "Monthly expenses") }) }), description: /* @__PURE__ */ jsx(FormHelp, { children: errors.monthlyExpenses }) })
    ] }) }) }),
    /* @__PURE__ */ jsx(Box, { css: {
      width: "382px",
      order: {
        base: -1,
        lg: 1
      },
      lg: {
        width: "auto"
      }
    }, children: /* @__PURE__ */ jsxs(Box, { css: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: "loose"
    }, children: [
      /* @__PURE__ */ jsx(Box, { css: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "loose"
      }, children: overBudget ? /* @__PURE__ */ jsxs(Fragment, { children: [
        /* @__PURE__ */ jsx(Text, { textStyle: "heading-sm", css: {
          color: "inherit"
        }, children: "You're over budget at" }),
        /* @__PURE__ */ jsx(Text, { textStyle: "heading-lg", css: {
          color: "text.feedback.critical.hero",
          marginBottom: "layout.looser"
        }, children: `$${rent.toLocaleString("en-US")} /mo` })
      ] }) : /* @__PURE__ */ jsxs(Fragment, { children: [
        /* @__PURE__ */ jsx(Text, { textStyle: "heading-sm", css: {
          color: "inherit"
        }, children: "You can afford" }),
        /* @__PURE__ */ jsx(Text, { textStyle: "heading-lg", css: {
          color: "text.feedback.success.hero",
          marginBottom: "layout.looser"
        }, children: `$${rent.toLocaleString("en-US")} /mo` })
      ] }) }),
      /* @__PURE__ */ jsxs(Box, { css: {
        display: "flex",
        alignItems: "center",
        width: "100%"
      }, children: [
        /* @__PURE__ */ jsx(Text, { css: {
          flex: 1,
          textAlign: "left",
          marginRight: "500",
          color: "inherit"
        }, children: "10%" }),
        /* @__PURE__ */ jsxs(Slider.Root, { id: "rent-value-slider", min: sliderMin, max: sliderMax, value: rent, step: sliderStep, tooltipText: percentOfIncome, onValueChange: (values) => handleSliderChange(values), onPointerUp: handleSliderCommit, onKeyUp: (e) => {
          if (["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "Home", "End", "PageUp", "PageDown"].includes(e.key)) {
            handleSliderCommit();
          }
        }, children: [
          /* @__PURE__ */ jsx(Slider.Track, { children: /* @__PURE__ */ jsx(Slider.ActiveTrack, {}) }),
          /* @__PURE__ */ jsx(Slider.Handle, { "aria-label": "Monthly rent amount", ariaValuetext: ({
            valueNow
          }) => `$${valueNow.toLocaleString("en-US")} per month, ${percentOfIncome} of income` })
        ] }),
        /* @__PURE__ */ jsx(Text, { css: {
          flex: 1,
          textAlign: "right",
          marginLeft: "500",
          color: "inherit"
        }, children: "40%" })
      ] }),
      overBudget ? /* @__PURE__ */ jsx(Paragraph, { css: {
        textAlign: "center",
        color: "inherit"
      }, children: "Careful! If you spend this much on rent, your debts and expenses will exceed your income." }) : /* @__PURE__ */ jsx(Paragraph, { css: {
        textAlign: "center",
        color: "inherit"
      }, children: "Based on your income, a rental at this price should fit comfortably within your budget." }),
      /* @__PURE__ */ jsxs(Paragraph, { css: {
        color: "inherit"
      }, children: [
        "You will have ",
        /* @__PURE__ */ jsx("strong", { children: `$${Math.round(incomeAndDebt.monthlyNetIncome - rent - incomeAndDebt.monthlyDebts - incomeAndDebt.monthlySavings - incomeAndDebt.monthlyExpenses).toLocaleString("en-US")} /mo` }),
        " left to spend."
      ] }),
      /* @__PURE__ */ jsx(Paragraph, { textStyle: "body-xs", css: {
        textAlign: "center",
        color: "inherit"
      }, children: "DISCLAIMER: The calculated output is just a suggestion. All personal and financial factors should be considered before signing a lease." })
    ] }) })
  ] }) });
};
export {
  RENT_AFFORDABILITY_STORAGE_KEY,
  RentAffordabilityCalculator as default
};
